import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import {
  actionTooltipProps,
  Dialog,
  DialogHeader,
  HubTable,
  Icon,
  ListToolbar,
  TableActionsMenu,
  TableSettingsDialog,
} from "@/ds";
import { HubTableCartao } from "@/shared/components/hub-table-cartao";
import { FiltersPopover } from "@/shared/components/filters-popover";

import { DetalheSimulacaoDialog } from "../detalhe-simulacao-dialog";
import {
  HISTORICO_COLUMNS,
  HISTORICO_EMPTY_NOUN,
  HISTORICO_EMPTY_PLURAL,
  HISTORICO_LOADING_LABEL,
} from "./historico-simulacao-dialog.constants";
import styles from "./historico-simulacao-dialog.module.scss";
import type {
  HistoricoColumnKey,
  HistoricoDataColumn,
  HistoricoLinha,
  HistoricoSimulacaoDialogViewModel,
  HistoricoSortField,
} from "./types-historico-simulacao-dialog";

const TOOLTIP_VER = actionTooltipProps("Detalhar", { event: "hover" });

function renderCell(coluna: HistoricoDataColumn, linha: HistoricoLinha) {
  return coluna === "status" ? <Tag value={linha.status} severity="info" /> : linha[coluna];
}

export function HistoricoSimulacaoDialogView({
  titulo,
  fechar,
  detalheId,
  abrirDetalhe,
  fecharDetalhe,
  filterColumns,
  filterValue,
  aplicarFiltros,
  linhas,
  total,
  status,
  errorMessage,
  table,
  settings,
  hasFilters,
  limparFiltros,
  tentarNovamente,
  avisarConfiguracoesAplicadas,
}: HistoricoSimulacaoDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="xl"
        className={styles.dialog}
        contentClassName={styles.conteudo}
        header={<DialogHeader title={titulo} />}
        footer={
          <Button
            type="button"
            label="Fechar"
            severity="secondary"
            outlined
            onClick={fechar}
            data-testid="historico-fechar"
          />
        }
        onHide={fechar}
        breakpoints={{ "76rem": "95vw", "48rem": "100vw" }}
        data-testid="historico-dialog"
      >
        <div className={styles.layout}>
          <ListToolbar.Root
            testId="historico-toolbar"
            framed={false}
            aria-label="Barra da listagem"
          >
            <ListToolbar.Filters>
              <FiltersPopover
                definitions={filterColumns}
                value={filterValue}
                onChange={aplicarFiltros}
                testId="historico-filtros"
              />
            </ListToolbar.Filters>

            <ListToolbar.Menu>
              <TableActionsMenu.Root testId="historico-menu">
                <TableActionsMenu.Trigger />
                <TableActionsMenu.SettingsItem onClick={settings.dialog.open} />
              </TableActionsMenu.Root>
            </ListToolbar.Menu>
          </ListToolbar.Root>

          <TableSettingsDialog
            dialog={settings.dialog}
            testId="historico-table-settings"
            onApplied={avisarConfiguracoesAplicadas}
          />

          <HubTableCartao<HistoricoLinha, HistoricoColumnKey, HistoricoSortField>
            value={linhas}
            totalRecords={total}
            table={table}
            settings={settings}
            status={status}
            errorMessage={errorMessage}
            loadingLabel={HISTORICO_LOADING_LABEL}
            emptyNoun={HISTORICO_EMPTY_NOUN}
            emptyGender="f"
            emptyPlural={HISTORICO_EMPTY_PLURAL}
            hasFilters={hasFilters}
            onClear={limparFiltros}
            onRetry={tentarNovamente}
            onRowClick={(linha) => abrirDetalhe(linha.id)}
            rowsPerPageLabel="Simulações por página"
            dataTableProps={{
              scrollable: true,
              scrollHeight: "flex",
              tableStyle: { minWidth: "52rem" },
              "aria-label": titulo,
            }}
            testId="historico"
          >
            {HISTORICO_COLUMNS.map((coluna) => (
              <HubTable.Column<HistoricoLinha, HistoricoColumnKey>
                key={coluna.key}
                field={coluna.key}
                header={coluna.header}
                sortable={false}
                style={{ minWidth: coluna.minWidth }}
                body={(linha) => renderCell(coluna.key, linha)}
              />
            ))}
            <HubTable.Column<HistoricoLinha, HistoricoColumnKey>
              field="acoes"
              header=""
              sortable={false}
              frozen
              alignFrozen="right"
              style={{ width: "3.5rem", minWidth: "3.5rem" }}
              body={(linha) => (
                <Button
                  type="button"
                  className={styles.botaoVer}
                  icon={<Icon name="search" size={14} />}
                  aria-label={linha.verLabel}
                  tooltip={TOOLTIP_VER.content as string}
                  tooltipOptions={{ position: TOOLTIP_VER.position, event: "hover" }}
                  onClick={() => abrirDetalhe(linha.id)}
                  data-testid="historico-ver"
                  severity="secondary"
                  text
                />
              )}
            />
          </HubTableCartao>
        </div>
      </Dialog>
      {/* O detalhe abre por cima: a lista segue montada e, ao voltar, mantém página e rolagem sem remedir a calha. */}
      {detalheId ? (
        <DetalheSimulacaoDialog simulacaoId={detalheId} onClose={fecharDetalhe} />
      ) : null}
    </>
  );
}
